import { useMemo } from 'react';
import { Typography } from '@mui/material';

import Tooltip from 'src/components/common/Tooltip';
import { MINTSCAN_URL } from 'src/constants/explorer';
import { useProposalsSWR } from 'src/hooks/SWR/cosmos/useProposalsSWR';
import { useVoteHistorySWR } from 'src/hooks/SWR/cosmos/useVoteHistorySWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { formatDate } from 'src/utils/string';

import {
  Base02TextContainer,
  IndicatorCircle,
  IndicatorContainer,
  LeftColumnContainer,
  LeftColumnSubTitleContainer,
  LeftColumnSubTitleItemContainer,
  LeftContainer,
  ProposalTitleContainer,
  ProposalTitleWrapperContainer,
  RightContainer,
  StyledProposalButton,
} from './styled';

import { ReactComponent as VoteStatusAbstainIcon } from 'src/assets/svg/VoteStatusAbstain.svg';
import { ReactComponent as VoteStatusNoIcon } from 'src/assets/svg/VoteStatusNo.svg';
import { ReactComponent as VoteStatusNotVotedIcon } from 'src/assets/svg/VoteStatusNotVoted.svg';
import { ReactComponent as VoteStatusVetoIcon } from 'src/assets/svg/VoteStatusVeto.svg';
import { ReactComponent as VoteStatusYesIcon } from 'src/assets/svg/VoteStatusYes.svg';

type ProposalButtonProps = {
  proposalId: string;
  onClick: (proposalId: string) => void;
};

export default function ProposalButton({ proposalId, onClick }: ProposalButtonProps) {
  const proposals = useProposalsSWR({ proposalStatus: '0' });

  const { currentAccount } = useCurrentAccount();

  const currentProposal = useMemo(() => proposals.data?.proposals?.find((item) => item.id === proposalId), [proposalId, proposals.data?.proposals]);

  const isVoting = useMemo(() => currentProposal?.status === 'PROPOSAL_STATUS_VOTING_PERIOD', [currentProposal?.status]);

  const isPassed = useMemo(() => currentProposal?.status === 'PROPOSAL_STATUS_PASSED', [currentProposal?.status]);

  const indicatorText = useMemo(() => {
    if (isVoting) {
      return '';
    }

    if (isPassed) {
      return 'PASSED';
    }

    return 'REJECTED';
  }, [isPassed, isVoting]);

  const votingEndTime = useMemo(() => formatDate(new Date(currentProposal?.voting_end_time || '')), [currentProposal?.voting_end_time]);

  const voteHistory = useVoteHistorySWR({ voterAddress: currentAccount.cosmosAddress });

  const votedStatusIcon = useMemo(() => {
    const voteHistoryData = voteHistory.data?.votes.find((item) => String(item.proposal_id) === currentProposal?.id);
    if (voteHistoryData) {
      if (voteHistoryData.votes[0].option === 'VOTE_OPTION_YES') {
        return <VoteStatusYesIcon />;
      }
      if (voteHistoryData.votes[0].option === 'VOTE_OPTION_NO') {
        return <VoteStatusNoIcon />;
      }
      if (voteHistoryData.votes[0].option === 'VOTE_OPTION_ABSTAIN') {
        return <VoteStatusAbstainIcon />;
      }
      if (voteHistoryData.votes[0].option === 'VOTE_OPTION_NO_WITH_VETO') {
        return <VoteStatusVetoIcon />;
      }
      if (voteHistoryData.votes[0].option === 'VOTE_OPTION_UNSPECIFIED') {
        return <VoteStatusNotVotedIcon />;
      }
    }
    return <VoteStatusNotVotedIcon />;
  }, [currentProposal?.id, voteHistory.data?.votes]);

  const errorMessage = useMemo(() => '', []);

  return (
    <Tooltip varient="error" title={errorMessage} placement="top" arrow>
      <div>
        <StyledProposalButton
          disabled={!!errorMessage}
          onClick={() => {
            if (currentProposal?.status === 'PROPOSAL_STATUS_VOTING_PERIOD') {
              onClick(proposalId);
            } else {
              window.open(`${MINTSCAN_URL}/proposals/${currentProposal?.id || ''}`);
            }
          }}
        >
          <LeftContainer>
            <LeftColumnContainer>
              <ProposalTitleWrapperContainer>
                <Typography variant="h4">{`#${currentProposal?.id || ''}. `}</Typography>
                &nbsp;
                <ProposalTitleContainer>
                  <Typography variant="h7">{` ${currentProposal?.title || '-'}`}</Typography>
                </ProposalTitleContainer>
              </ProposalTitleWrapperContainer>

              <LeftColumnSubTitleContainer>
                <LeftColumnSubTitleItemContainer>
                  {isVoting ? (
                    <Base02TextContainer>
                      <Typography variant="h9">{votingEndTime}</Typography>
                    </Base02TextContainer>
                  ) : (
                    <IndicatorContainer>
                      <IndicatorCircle data-is-passed={isPassed} />

                      <Typography variant="h8">{indicatorText}</Typography>
                    </IndicatorContainer>
                  )}
                </LeftColumnSubTitleItemContainer>
              </LeftColumnSubTitleContainer>
            </LeftColumnContainer>
          </LeftContainer>

          <RightContainer>{votedStatusIcon}</RightContainer>
        </StyledProposalButton>
      </div>
    </Tooltip>
  );
}
